/**
 * WHO ACTUALLY CAPS A DEPOSIT, AND WHERE.
 *
 * THE MISTAKE THIS FILE EXISTS TO UNDO. The first version of the deposit policy applied Seattle's
 * cap to every home on the policy — including Medina, Mercer Island, Bellevue and Kirkland, which
 * are not Seattle and are not governed by a Seattle ordinance. A reviewer caught it, and checking
 * it properly turned up that the error ran the other way too.
 *
 * WHAT IS ACTUALLY TRUE, looked up rather than assumed (2026-09-27):
 *
 *   · WASHINGTON STATE SETS NO CAP AT ALL. RCW 59.18.253–.285 govern how a deposit is held,
 *     documented and returned, and say nothing about how large it may be. So outside a city with
 *     its own rule there is no ceiling in law.
 *   · SEATTLE DOES. SMC 7.24.020 limits the security deposit PLUS any non-refundable move-in fees
 *     to the first month's rent. Not the deposit alone — which is the second half of the same
 *     mistake, because a $1,500 deposit and a $500 cleaning fee on a $1,750 home is over the line
 *     while the deposit by itself is not.
 *
 * SO THE CAP IS A PROPERTY OF THE HOME'S CITY, NOT OF THIS COMPANY. It is looked up, and a city
 * with no entry gets NO CAP rather than a borrowed one.
 *
 * AND AN UNKNOWN CITY IS NOT A SAFE CITY. `capFor` never answers with a bare absence: it returns
 * `{ kind: 'CAP' | 'NONE' | 'UNKNOWN' }` and callers branch on which. "This city has no rule" and
 * "I do not know where this home is" are different facts, and a screen that reports those two the
 * same way tells somebody a home was checked when nobody checked it. An earlier version of this
 * comment described a `null` return, which the function never had — see the note on `capFor`.
 */

/**
 * The rules this product knows. One entry today, and that is the honest state of it.
 *
 * `limit` returns the ceiling in dollars given the home's monthly rent, so a city that caps at some
 * other multiple can be added without changing anything that calls this.
 */
const RULES = [
  {
    id: 'SEATTLE_SMC_7_24_020',
    /*
      WHAT A PERSON READS, WHICH IS NOT THE ID. The id is for code; a warning on a quote that
      opened with `SEATTLE_SMC_7_24_020` was naming an internal constant at somebody trying to
      decide whether to send. `label` is the citation as a lawyer would write it. (mtr#112.)
    */
    label: 'SMC 7.24.020',
    city: 'seattle',
    says: "Seattle caps the deposit plus any non-refundable move-in fees at the first month's rent (SMC 7.24.020).",
    limit: ({ monthlyRent }) => monthlyRent,
  },
]

const key = (city) => String(city ?? '').trim().toLowerCase()

/**
 * The cap that applies to this home, or why there is none.
 *
 * Returns one of:
 *   `{ kind: 'CAP', rule, limit }`      — a rule applies and this is the ceiling in dollars
 *   `{ kind: 'NONE', city }`            — we know the city and it has no rule (Washington has none)
 *   `{ kind: 'UNKNOWN', missing: [] }`  — something needed to answer is not recorded
 *
 * THREE ANSWERS AND NOT TWO, deliberately. "No rule here" and "I do not know where this is" are
 * different facts, and collapsing them is how a screen ends up saying a home is fine when the
 * truth is that nothing was checked.
 *
 * AND `UNKNOWN` SAYS WHICH FACT IS MISSING. (Copilot, mtr#95.) Two different gaps produce it — no
 * city, and no monthly rent to measure a ceiling against — and the first version reported both as
 * "no city recorded". A home in Seattle whose rent nobody has typed was then described as a home
 * nobody knows the location of, which sends whoever reads the warning to fix the wrong field.
 * `missing` names them, and both can be true at once.
 */
export function capFor({ city, monthlyRent }) {
  /*
    THE CITY IS ASKED FIRST, BECAUSE A CITY WITH NO RULE NEEDS NOTHING ELSE. (Copilot, mtr#95.)

    An earlier version demanded a rent before it would look anything up, so a Bellevue home whose
    rent nobody had typed came back `UNKNOWN` and was reported as unchecked — when the answer is
    known and complete: Washington sets no cap and Bellevue has no ordinance of its own, so there
    is no ceiling here whatever the rent is. It also made a flat-amount policy depend on a rent it
    never uses.

    The rent is required only where a rule exists AND is expressed in terms of it, which today is
    every rule there is — but asking `rule.limit` for a figure it cannot compute is the narrower
    question, and it is the one that will still be right when a city caps at a flat sum.
  */
  if (key(city) === '') return { kind: 'UNKNOWN', missing: ['city'] }

  const rule = RULES.find((r) => r.city === key(city))
  if (!rule) return { kind: 'NONE', city }

  const limit = rule.limit({ monthlyRent })
  if (!Number.isFinite(limit)) return { kind: 'UNKNOWN', missing: ['rent'] }
  return { kind: 'CAP', rule, limit }
}
